import React, { createContext, useContext, useMemo } from 'react';
import { ChatApiUrls } from '../types/chat';

// Import the new master hook and its return type
import { useChat, ChatController } from '@/features/chat/hooks/useChat';
import { useAuth } from '@/contexts/auth-context';
import { createChatApiService } from '@/services/chatApiService';
import { createMockChatApiService } from '@/services/__mocks__/chatApiService';
import { USE_MOCK_CHAT_RESPONSE, MOCK_RATE_LIMIT } from '@/config';
import { useRateLimit } from '@/hooks/useRateLimit';
// Removed resolveApiUserIdSafe - server handles dev user ID resolution

// The context now holds the return value of our master hook
// The type is inferred from the ChatController type for simplicity and accuracy.
export const ChatContext = createContext<ChatController | undefined>(undefined);

// Safe no-op stub used only during server-side rendering/prerender
const serverSideChatStub: ChatController = {
  // Sessions
  userId: null,
  isUserLoading: false,
  chatSessions: [],
  setChatSessions: () => {},
  activeChatId: null,
  setActiveChatId: () => {},
  isLoading: false,
  updateChatSession: () => {},
  createNewChat: async () => null,
  deleteChat: async () => {},
  deleteSelectedChats: async () => {},
  saveConversationsToCache: () => {},
  chatSessionsRef: { current: [] },
  isSelectionMode: false,
  setSelectionMode: () => {},
  selectedChatIds: [],
  toggleChatSelection: () => {},
  selectAllChats: () => {},
  clearSelectedChats: () => {},
  startEditingTitle: () => {},
  cancelEditingTitle: () => {},
  saveChatTitle: async () => {},
  editingChatId: null,
  editingChatTitle: '',
  setEditingChatTitle: () => {},
  openLastConversation: () => {},

  // Messages
  messages: [],
  messageVisibility: {},
  toggleMessageVisibility: () => {},
  addOptimisticMessage: () => {},
  updateOptimisticMessage: () => {},
  removeMessagesAfterIndex: () => {},
  saveMessagesToCache: () => {},

  // Actions
  isResponding: false,
  currentTaskId: null,
  sendMessage: async () => {},
  stopStreaming: async () => {},
  handleRegenerate: async () => {},
  sendFeedback: async () => {},
  handleSaveEditAction: async () => {},

  // UI
  isSidebarOpen: false,
  toggleSidebar: () => {},
  openSidebar: () => {},
  closeSidebar: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
  inputMessage: '',
  setInputMessage: () => {},
  messageHoverStates: {},
  handleMessageHover: () => {},
  replyingToMessage: null,
  handleReplyToMessage: () => {},
  cancelReply: () => {},

  // Files
  isUploading: false,
  selectedFiles: [],
  setSelectedFiles: () => {},
  setIsUploading: () => {},
  handleFileSelect: async () => {},
  handleFileDrop: async () => {},
  removeFile: () => {},

  // Editing
  isEditingMessage: false,
  messageBeingEdited: null,
  editedMessageContent: '',
  setEditedMessageContent: () => {},
  handleEditMessage: () => {},
  handleCancelEdit: () => {},
  handleSaveEdit: async () => {},

  // Aggregated
  activeChat: undefined,
  initialMessage: null,
  setInitialMessage: () => {},
  handleFileUpload: async () => {},
};

// The public hook to consume the context is renamed to avoid confusion.
export const useChatContext = () => {
  const context = useContext(ChatContext);

  if (!context) {
    // During build/prerender (server-side), avoid throwing so pages can be rendered
    if (typeof window === 'undefined') {
      return serverSideChatStub;
    }

    // On the client, still enforce correct provider usage
    throw new Error('useChatContext must be used within a ChatProvider');
  }

  return context;
};

interface ChatProviderProps {
  children: React.ReactNode;
  apiUrls: ChatApiUrls;
}

// Extended context type that includes rate limit dialog
export interface ChatContextWithRateLimit extends ChatController {
  RateLimitDialogComponent: React.ReactNode;
}

// The provider is now a simple wrapper around the master useChat hook.
// It injects the appropriate service (mock or production) based on environment.
export const ChatProvider: React.FC<ChatProviderProps> = ({ children, apiUrls }) => {
  const { user, isInitialized, isLoading } = useAuth();
  
  // Create service based on environment
  // Mock service is used in development when USE_MOCK_CHAT_RESPONSE is enabled
  const chatApiService = useMemo(() => {
    if (USE_MOCK_CHAT_RESPONSE) {
      console.log('[ChatProvider] Using mock chat API service', MOCK_RATE_LIMIT ? '(with rate limit enabled)' : '');
      return createMockChatApiService({
        shouldRateLimit: MOCK_RATE_LIMIT,
      });
    }
    return createChatApiService(apiUrls);
  }, [apiUrls]);

  // Create shared rate limit hook instance
  const rateLimit = useRateLimit();
  const { RateLimitDialogComponent, handleRateLimitError } = rateLimit;

  const chatController = useChat({
    chatApiService,
    // Server will resolve dev user ID if user?.id is null in dev mode
    userId: user?.id ?? null,
    isUserLoading: !isInitialized || isLoading,
    handleRateLimitError, // Pass down to useChatActions
  });

  // Extend chatController with rate limit dialog
  const extendedContext: ChatContextWithRateLimit = useMemo(() => ({
    ...chatController,
    RateLimitDialogComponent,
  }), [chatController, RateLimitDialogComponent]);

  return (
    <ChatContext.Provider value={extendedContext as ChatController}>
      {children}
    </ChatContext.Provider>
  );
};